import ConfirmDialog, { PropsType as ConfirmDialogPropsType } from '@/components/ConfirmDialog';
import SearchCourse from '@/components/SearchCourse';
import Constants from '@/Constants';
import { useGet } from '@/hooks/useGet';
import { useGetPaginated } from '@/hooks/useGetPaginated';
import { CourseType } from '@/models/CourseLessonContentTO';
import { EntityType } from '@/models/EntityTypes';
import List from '@/models/List';
import { Program } from '@/models/Program';
import { ProgramClass } from '@/models/ProgramClass';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import { Identity } from '@/store/identity/types';
import { getProgramClear, getProgramRequest, publishProgramRequest } from '@/store/program/action';
import { LocaleData } from '@/translations/LocaleData';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'underscore';
import CourseList from './CourseList';
import './style.scss';

interface CourseSearchOption {
    toogle: boolean;
    toogleFunction: CallableFunction;
    collapseSectionId: string;
    icon: string;
    label: string;
    ref: any;
    courseType: CourseType;
    hasResults: boolean;
}

const ProgramContents: React.FC = () => {
    const program: Program = useSelector((state: ApplicationState) => state.program.programData);
    const courses: List[] = useSelector((state: ApplicationState) => state.program.courses);
    const strings: LocaleData = useSelector((state: ApplicationState) => state.locale.data);
    const identity: Identity = useSelector((state: ApplicationState) => state.identity.data);
    const orgId: number = useSelector((state: ApplicationState) => state.identity.data.user.orgId);

    const [preReqEnabled, setPreReqEnabled] = useState<boolean>(false);
    const [workshopCourseSearchVisibility, setWorkshopCourseSearchVisibility] = useState<boolean>(false);
    const [regularCourseSearchVisibility, setRegularCourseSearchVisibility] = useState<boolean>(false);
    const [searchName, setSearchName] = useState<string>('');
    const [onPrevPressed, setOnPrevPressed] = useState<boolean>(false);
    const [onNextPressed, setOnNextPressed] = useState<boolean>(false);
    const [onSavePressed, setOnSavePressed] = useState<boolean>(false);
    const [isClosedAndPublishedProgram, setIsClosedAndPublishedProgram] = useState<boolean>(false);
    const [publishAllowed, setPublishAllowed] = useState<boolean>(false);
    const [showWorkshopAddedModal, setShowWorkshopAddedModal] = useState<boolean>(false);
    const [coursesBkp, setCoursesBkp] = useState<List[]>([]);
    const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogPropsType>({} as ConfirmDialogPropsType);

    const buttonRegularRef = useRef<HTMLAnchorElement>(null);
    const buttonWorkshopRef = useRef<HTMLAnchorElement>(null);

    const history = useHistory();
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();

    const getKey = () => {
        const {
            user: { orgId, applicationId },
        } = identity;

        if (!program.categories) {
            return null;
        }

        return (
            Constants.LMS_COURSES_URI +
            courseService.getQueryParams({
                type: 'WORKSHOP',
                orgId: orgId,
                applicationId: applicationId,
                status: Constants.programStatus.PUBLISHED,
                locale: program.locale,
                onlyHasProgramClass: 0,
                moduleId: Constants.LMS_MODULE_ID,
                limit: 0,
                offset: 0,
            })
        );
    };

    const { total } = useGetPaginated<List>(getKey);

    const { data: programClasses } = useGet<ProgramClass[]>(
        `${Constants.LMS_PROGRAM_CLASS_URI}?programId=${id}&orgId=${orgId}&withEnrollmentsCount=true`,
        {
            revalidateOnMount: true,
        },
    );

    useEffect(() => {
        if (coursesBkp?.length === 0) {
            setCoursesBkp([...courses]);
        }
    }, [courses]);

    useEffect(() => {
        if (id) {
            dispatch(getProgramRequest(parseInt(id)));
        } else {
            dispatch(getProgramClear());
        }
    }, [dispatch, id]);

    useEffect(() => {
        setPublishAllowed(program.status === Constants.programStatus.DRAFT && courses.length > 0);
    }, [program, courses]);

    useEffect(() => {
        setIsClosedAndPublishedProgram(program.status === Constants.programStatus.PUBLISHED && program.type === Constants.programTypes.READY);
    }, [program]);

    useEffect(() => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                currentStep: 2,
                nextButtonTitle: strings.PUBLISH,
                onNext: () => setOnNextPressed(true),
                onPrevious: () => setOnPrevPressed(true),
                onSave: () => setOnSavePressed(true),
                saveDisabled: false,
                previousButtonTitle: strings.PREVIOUS,
                saveButtonTitle: strings.SAVE_AND_CLOSE,
                totalSteps: 2,
                nextDisabled: !publishAllowed,
                previewDisabled: false,
                blockUi: false,
            }),
        );
    }, [strings._LANGUAGE, publishAllowed]);

    useEffect(() => {
        if (onPrevPressed) {
            dispatch(
                setFloatingCourseActionsConfiguration({
                    previousButtonTitle: strings.LOADING,
                    blockUi: true,
                } as FloatingCourseActionsBarConfiguration),
            );
            history.push(`/contents/lms/programs/${id || program.id}`);
        }

        if (onNextPressed) {
            setConfirmDialogData({
                title: strings.PROGRAM_PUBLISH_MODAL_TITLE,
                content: strings.PROGRAM_PUBLISH_MODAL_SUBTITLE,
                leftButtonTitle: strings.NO,
                rightButtonTitle: strings.YES,
                icon: Constants.icons.CHECK_CIRCLE,
                color: '#3f9b35',
                show: true,
                leftClick: () => history.push(`/contents/lms/programs`),
                rightClick: () => history.push(`/contents/lms/programs/${id}/classes`),
            } as ConfirmDialogPropsType);

            dispatch(publishProgramRequest(parseInt(id)));
        }

        if (onSavePressed && showWorkshopAddedModal) {
            setConfirmDialogData({
                title: strings.PROGRAM_WITH_NEW_WORKSHOP_WARNING,
                content: strings.DO_YOU_WANT_TO_CONFIGURE_NOW,
                leftButtonTitle: strings.NO,
                rightButtonTitle: strings.YES,
                icon: Constants.icons.REPORT_PROBLEM,
                show: true,
                leftClick: () => history.push(`/contents/lms/programs`),
                rightClick: () => history.push(`/contents/lms/programs/${id}/classes`),
                color: '#f10',
            } as ConfirmDialogPropsType);
        } else if (onSavePressed) {
            history.push(`/contents/lms/programs`);
        }
    }, [onPrevPressed, onNextPressed, onSavePressed]);

    useEffect(() => {
        if (courses.length > 0 && coursesBkp.length > 0 && !_.isEqual(courses, coursesBkp)) {
            const isBuildingProgram = program.type === 'BUILDING';
            const hasProgramClassStarted = (programClasses || []).some((c) => c.startDate);
            const hasNewWorkshopAdded: boolean = courses.filter((i) => coursesBkp.indexOf(i) === -1).some((c) => c.type === EntityType.WORKSHOP);

            setShowWorkshopAddedModal(hasNewWorkshopAdded && isBuildingProgram && hasProgramClassStarted);
        }
    }, [courses?.length]);

    const closedProgramWarningHandler = () =>
        isClosedAndPublishedProgram && (
            <div className="closed-program-info">
                <i className="material-icons icon-error">{Constants.icons.ERROR_OUTLINE}</i>
                <span className="error-info">{strings.PUBLISHED_AND_CLOSED_PROGRAM_INFO}</span>
            </div>
        );

    const handleMenuHeaderOpacity = (): number => {
        return isClosedAndPublishedProgram ? 0.3 : 1;
    };

    const toogleRegular = useCallback(() => {
        setRegularCourseSearchVisibility(!regularCourseSearchVisibility);
        if (workshopCourseSearchVisibility) {
            setWorkshopCourseSearchVisibility(false);
            buttonWorkshopRef.current?.click();
        }
    }, [regularCourseSearchVisibility, workshopCourseSearchVisibility, buttonWorkshopRef]);

    const toogleWorkShop = useCallback(() => {
        setWorkshopCourseSearchVisibility(!workshopCourseSearchVisibility);
        if (regularCourseSearchVisibility) {
            setRegularCourseSearchVisibility(false);
            buttonRegularRef.current?.click();
        }
    }, [workshopCourseSearchVisibility, regularCourseSearchVisibility, buttonRegularRef]);

    const searchSections: CourseSearchOption[] = [
        {
            toogle: regularCourseSearchVisibility,
            toogleFunction: toogleRegular,
            collapseSectionId: 'collapseRegular',
            icon: Constants.icons.PUBLIC,
            label: strings.COURSE_ONLINE,
            ref: buttonRegularRef,
            courseType: 'REGULAR',
            hasResults: true,
        },

        {
            toogle: workshopCourseSearchVisibility,
            toogleFunction: toogleWorkShop,
            collapseSectionId: 'collapseWorkshop',
            icon: Constants.icons.GROUP,
            label: strings.WORKSHOP,
            ref: buttonWorkshopRef,
            courseType: 'WORKSHOP',
            hasResults: total > 0,
        },
    ];

    return (
        <>
            <ConfirmDialog
                color={confirmDialogData.color}
                content={confirmDialogData.content}
                icon={confirmDialogData.icon}
                leftButtonTitle={confirmDialogData.leftButtonTitle}
                leftClick={confirmDialogData.leftClick}
                rightButtonTitle={confirmDialogData?.rightButtonTitle}
                rightClick={confirmDialogData.rightClick}
                show={confirmDialogData.show || false}
                title={confirmDialogData.title}
            />

            <div className="track-contents-main-container">
                <div className="left-panel">
                    {closedProgramWarningHandler()}
                    {searchSections.map((item: CourseSearchOption, index: number) => (
                        <>
                            {item.hasResults && (
                                <div className="menu-header" key={item.collapseSectionId + index} style={{ opacity: handleMenuHeaderOpacity() }}>
                                    <button
                                        aria-controls="collapseExample"
                                        aria-expanded="false"
                                        className="btn btn-link new-program-course"
                                        data-target={'#' + item.collapseSectionId}
                                        data-toggle="collapse"
                                        disabled={isClosedAndPublishedProgram}
                                        onClick={() => item.toogleFunction()}
                                        ref={item.ref}
                                    >
                                        {item.toogle ? <div className="arrow-up" /> : <div className="arrow-down" />}
                                        <i className="material-icons-outlined align-middle icon-person">{item.icon}</i>
                                        <span className="new-class-button">{item.label}</span>
                                    </button>
                                </div>
                            )}
                            <div className="menu-content" id={`content_${item.collapseSectionId + index} `}>
                                <div className="collapse" id={item.collapseSectionId}>
                                    <div className="search-course" id={`course_${item.collapseSectionId + index} `}>
                                        {program.categories && (
                                            <SearchCourse
                                                categories={program.categories}
                                                courseShow={item.toogle}
                                                limit={3}
                                                locale={program.locale}
                                                preReqEnabled={preReqEnabled}
                                                programId={parseInt(id)}
                                                setValue={setSearchName}
                                                type={item.courseType}
                                                value={searchName}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    ))}
                </div>

                <div className="right-panel">
                    <div className="title-container">
                        <span className="title-text">{strings.PROGRAM_CONTENT}</span>
                        <CourseList preReqEnabled={preReqEnabled} setPreReqEnabled={setPreReqEnabled} />
                    </div>
                </div>
            </div>
        </>
    );
};
export default ProgramContents;
